import {Icon} from '@ezcater/tapas';
import {faUpRightFromSquare} from '@fortawesome/pro-solid-svg-icons';
import {twJoin} from 'tailwind-merge';

import {splitOnMatches} from '@/utils/helpCenter/searchArticles';
import {type HelpCenterArticle} from '@/utils/helpCenter/types';
import articleSnippet from './articleSnippet';

type ArticleLinkProps = {
  article: HelpCenterArticle;
  /** When set, matching terms in the title and snippet are emphasised. */
  query?: string;
  /** Search results render as bordered cards with a snippet; drill-down rows render as links. */
  variant?: 'card' | 'row';
  onSelect?: (article: HelpCenterArticle) => void;
};

const Highlighted: React.FC<{text: string; query?: string}> = ({text, query}) => {
  if (!query) return <>{text}</>;
  return (
    <>
      {splitOnMatches(text, query).map((segment, index) => (
        <span
          key={`${segment.text}-${index}`}
          className={segment.matched ? 'font-bold' : undefined}
        >
          {segment.text}
        </span>
      ))}
    </>
  );
};

const ArticleLink: React.FC<ArticleLinkProps> = ({article, query, variant = 'row', onSelect}) => {
  const snippet = articleSnippet(article, query);

  return (
    <a
      href={article.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => onSelect?.(article)}
      className={twJoin(
        'group rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-700 focus-visible:ring-offset-2',
        variant === 'card'
          ? 'flex flex-col gap-2 border border-peppercorn-100 p-4 transition-colors hover:bg-peppercorn-50'
          : 'flex items-start gap-2 py-2',
      )}
      data-testid="help-article-link"
    >
      <span className="flex items-start gap-2 text-base font-bold text-peppercorn-800">
        <span
          className={
            variant === 'row'
              ? 'underline-offset-2 group-hover:underline group-focus-visible:underline'
              : undefined
          }
        >
          <Highlighted text={article.title} query={query} />
        </span>
        <Icon
          icon={faUpRightFromSquare}
          className={twJoin(
            'mt-1.5 shrink-0 text-xs text-peppercorn-800',
            variant === 'row' &&
              'opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100',
          )}
        />
      </span>
      {variant === 'card' && snippet && (
        <span className="line-clamp-3 text-sm text-peppercorn-500">
          <Highlighted text={snippet} query={query} />
        </span>
      )}
    </a>
  );
};

export default ArticleLink;
